import { Observable } from 'rxjs';
import { BaseBudgetImportService } from '../../../../core/services/base-budget-import.service';
import { ExcelImportModalBindings } from '../excel-import-modal/excel-import-modal.model';
import {
  BudgetImportHelper,
  CurrentBudgetTables,
} from './budget-import.helper';
import {
  BudgetImportProjektpart,
  BudgetImportResult,
  BudgetSlotSchema,
} from './budget-import.interface';
import { BUDGET_IMPORT_TEXT } from './budget-import.text';

/** Wires BudgetSlotSchema/BaseBudgetImportService into ExcelImportModalShellComponent's initialState. */
export function buildBudgetImportBindings(
  schema: BudgetSlotSchema,
  currentTables: Map<string, CurrentBudgetTables>,
  importService: BaseBudgetImportService,
): ExcelImportModalBindings {
  return {
    texts: BUDGET_IMPORT_TEXT,
    years: BudgetImportHelper.years(schema),
    deploymentItems: [
      { value: 'excel-test', label: 'gpt-5' },
      { value: 'Mistral-Large-3', label: 'Mistral-Large-3' },
      { value: 'gpt-5.4-mini', label: 'gpt-5.4-mini' },
      { value: 'gpt-5.6-luna', label: 'gpt-5.6-luna' },
    ],
    supportsPreprocessing: true,
    analyzeFn: (file, options) =>
      importService.importBudget(
        file,
        schema,
        options.reasoningEffort,
        options.deployment,
        options.preprocessing,
      ) as Observable<unknown>,
    buildPreviewFn: (result, unit, assignments) => {
      // The backend never echoes arbetsstalleNamn/adress back, so restore them from the schema we sent.
      const schemaById = new Map(schema.projektparter.map((p) => [p.id, p]));
      const enrichedResult: BudgetImportResult = {
        ...(result as BudgetImportResult),
        projektparter: (result as BudgetImportResult).projektparter.map(
          (p) => ({
            ...p,
            arbetsstalleNamn: schemaById.get(p.id)?.arbetsstalleNamn,
            adress: schemaById.get(p.id)?.adress,
          }),
        ),
      };
      return BudgetImportHelper.buildPreview(
        enrichedResult,
        unit,
        currentTables,
        assignments,
      ).map((p) => ({
        part: p.part,
        hasImportedValues: p.hasImportedValues,
        sourceRows: p.sourceRows,
        slotOptions: p.slotOptions,
        sections: [
          { title: BUDGET_IMPORT_TEXT.kostnader, rows: p.kostnader },
          { title: BUDGET_IMPORT_TEXT.finansiering, rows: p.finansiering },
        ],
      }));
    },
    getUnitFn: (result) => (result as BudgetImportResult).unit ?? 'SEK',
    getUnmatchedBlocksFn: (result) =>
      (result as BudgetImportResult).unmatchedBlocks,
    getWarningsFn: (result) => (result as BudgetImportResult).warnings,
    toApplyResultFn: (includedParts) => ({
      projektparter: BudgetImportHelper.toApplyParts(
        includedParts.map((p) => ({
          part: p.part as BudgetImportProjektpart,
          kostnader: p.sections[0]?.rows ?? [],
          finansiering: p.sections[1]?.rows ?? [],
          hasImportedValues: p.hasImportedValues,
        })),
      ),
    }),
  };
}
